import { useEffect } from 'react';
import {bool, func, shape} from 'react-proptypes';
import {useDispatch, useSelector} from '../hooks';
import analytics from '../utility/analytics';
import showPensionIncome from '../helpers/showPensionIncome';
import {Hyperlink} from '../buttons';
import {Heading, Info, LabelText} from '../components';
import {Checkbox, CurrencyInput, Income, RadioSwitch, SplitDate} from '../inputs';
import {SYNC, sync} from '../wizard/actions';
import {FlexContainer, InputsContainer} from '../containers';

const ApplicantForm = () => {
  const dispatch = useDispatch();
  const {
    output: {
      aow_age_reached,
      aow_within_ten_years,
      errors
    },
    settings: {
      entrepreneursModule
    }, app, input
  } = useSelector(state => state)
  const syncAction = sync(dispatch);

  useEffect(() => {
    dispatch({ type: SYNC, syncing: false });
    analytics.send('Navigeer', 'Applicant Pagina');
  }, []);

  const onDateHandler = date => {
    syncAction('applicant_dob', date);

    showPensionIncome(app, date).then(res => {
      dispatch({
        type: SYNC,
        payload: {
          aow_age_reached: Boolean(res?.data?.aow_age_reached),
          aow_within_ten_years: Boolean(res?.data?.aow_within_ten_years)
        }
      })
    });
  };

  return (
    <>
      <Heading>Je gegevens</Heading>
      <InputsContainer>
        <LabelText>Geboortedatum</LabelText>
        <SplitDate
          name="dob"
          onChange={onDateHandler}
          value={input.applicant_dob}
          errors={errors.applicant_dob}
        />

        {entrepreneursModule && !aow_age_reached ? (
         <>
           <LabelText>
             Ben je ondernemer?
             <Info title='Ben je ondernemer?'>
               Voor de berekening van je toetsinkomen hebben we de nettowinst van
               de afgelopen 3 kalenderjaren nodig (jaar 3 is het meest recente volledige kalenderjaar).
             </Info>
           </LabelText>
           <RadioSwitch
             name={"applicant_is_entrepreneur"}
             onChangeHandler={() => syncAction('applicant_is_entrepreneur', !input.applicant_is_entrepreneur)}
             checked={input.applicant_is_entrepreneur}
             errors={errors.applicant_is_entrepreneur}
           />

           {input.applicant_is_entrepreneur && (
             <ApplicantIsEntrepreneur input={input} sync={syncAction} errors={errors} />
           )}
         </>
        ) : null}

        {(!input.applicant_is_entrepreneur && (aow_within_ten_years || !aow_age_reached)) && (
          <ApplicantIncome input={input} sync={syncAction} errors={errors} aowWithinTenYears={aow_within_ten_years} />
        )}

        {((aow_within_ten_years || aow_age_reached)) && (
          <PensionIncome input={input} sync={syncAction} isRetired={aow_age_reached} errors={errors} />
        )}

      </InputsContainer>
    </>
  );
};

ApplicantForm.defaultProps = {
  output: {
    aow_age_reached: false,
    aow_within_ten_years: false,
  },
  errors: {},
  recalculating: null,
};


const ApplicantIsEntrepreneur = ({ input, sync, errors }) => (
  <>
    <LabelText>Nettowinst {new Date().getFullYear() - 3}</LabelText>
    <CurrencyInput
      id="applicant_entrepreneur_fiscal_profit_three_years_ago"
      onChange={sync('applicant_entrepreneur_fiscal_profit_three_years_ago')}
      value={input.applicant_entrepreneur_fiscal_profit_three_years_ago}
      errors={errors.applicant_entrepreneur_fiscal_profit_three_years_ago}
      placeholder="Bedrag"
    />
    <LabelText>Nettowinst {new Date().getFullYear() - 2}</LabelText>
    <CurrencyInput
      id="applicant_entrepreneur_fiscal_profit_two_years_ago"
      onChange={sync('applicant_entrepreneur_fiscal_profit_two_years_ago')}
      value={input.applicant_entrepreneur_fiscal_profit_two_years_ago}
      errors={errors.applicant_entrepreneur_fiscal_profit_two_years_ago}
      placeholder="Bedrag"
    />
    <LabelText>Nettowinst {new Date().getFullYear() - 1}</LabelText>
    <CurrencyInput
      id="applicant_entrepreneur_fiscal_profit_last_year"
      onChange={sync('applicant_entrepreneur_fiscal_profit_last_year')}
      value={input.applicant_entrepreneur_fiscal_profit_last_year}
      errors={errors.applicant_entrepreneur_fiscal_profit_last_year}
      placeholder="Bedrag"
    />
  </>
);

ApplicantIsEntrepreneur.propTypes = {
  sync: func.isRequired,
  input: shape({}).isRequired,
  errors: shape({}).isRequired,
};

const ApplicantIncome = ({ input, sync, aowWithinTenYears, errors }) => {
  const incomeText = aowWithinTenYears ? 'Bruto jaarinkomen voor AOW' : 'Bruto inkomen';

  return (
    <>
      <LabelText>{incomeText}</LabelText>
      <Income
        id="applicant_income"
        onAmountChangeHandler={sync('applicant_income')}
        onPeriodChangeHandler={option => sync('applicant_income_period', option.value)}
        amount={input.applicant_income}
        period={input.applicant_income_period}
        errors={errors.applicant_income}
      />
      {input.applicant_income_period === 'monthly' && (
        <FlexContainer>
          <Checkbox
            name="applicant_holiday_bonus"
            onChange={sync('applicant_holiday_bonus')}
            value={input.applicant_holiday_bonus}
            label="Vakantiegeld?"
            errors={errors.applicant_holiday_bonus}
          />
          <Checkbox
            name="applicant_13th_month_salary"
            onChange={sync('applicant_13th_month_salary')}
            value={input.applicant_13th_month_salary}
            label="13e maand?"
            errors={errors.applicant_13th_month_salary}
          />
        </FlexContainer>
      )}
    </>
  )
};

ApplicantIncome.propTypes = {
  sync: func.isRequired,
  aowWithinTenYears: bool,
  input: shape({}).isRequired,
  errors: shape({}).isRequired,
};

const PensionIncome = ({ input, errors, isRetired, sync }) => {
  const labelText = isRetired ? 'Bruto inkomen (inclusief AOW)' : 'Bruto inkomen na AOW datum (inclusief AOW)';

  return (
    <>
      <LabelText>
        {labelText}
        <Info title='Hoogte pensioen en AOW'>
          Op{' '}
          <Hyperlink href="https://www.mijnpensioenoverzicht.nl/">
            mijnpensioenoverzicht.nl
          </Hyperlink>{' '}
          vind je informatie over je AOW en je pensioen via al je werkgevers (oud
          en nieuw). Let op: je vindt hier geen informatie over een aanvullend pensioen
          dat je zelf hebt geregeld.
        </Info>
      </LabelText>
      <Income
        id="applicant_pension_income"
        onAmountChangeHandler={sync('applicant_pension_income')}
        onPeriodChangeHandler={option => sync('applicant_pension_income_period', option.value)}
        amount={input.applicant_pension_income}
        period={input.applicant_pension_income_period}
        errors={errors.applicant_pension_income}
      />
    </>
  );
}

PensionIncome.propTypes = {
  sync: func.isRequired,
  isRetired: bool,
  input: shape({}).isRequired,
  errors: shape({}).isRequired,
};

export default ApplicantForm;
